import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useBuilder } from '../lib/builderStore';
import { Badge, Checkbox, Field, Input, Select, Spinner } from '../components/ui';
import { Link } from 'react-router-dom';
import { Notice, Page, PageHeader, Section, Stack } from '../components/Page';

/**
 * Form settings, with protection first.
 *
 * Protection leads because it is the only group here where a wrong value fails silently:
 * a bad progress-indicator choice is visible immediately, whereas a captcha left off is
 * invisible until the spam arrives.
 */
export function Settings() {
  const { formId = '' } = useParams();
  const queryClient = useQueryClient();

  const definition = useBuilder((s) => s.definition);
  const loadedFormId = useBuilder((s) => s.formId);
  const load = useBuilder((s) => s.load);
  const apply = useBuilder((s) => s.apply);
  const dirty = useBuilder((s) => s.dirty);
  const markSaved = useBuilder((s) => s.markSaved);

  const draft = useQuery({
    queryKey: ['definition', formId],
    queryFn: () => api.forms.getDefinition(formId),
    enabled: Boolean(formId),
  });

  // What the server has configured, which is not part of the form definition: an author
  // needs to know whether turning a captcha "on" will actually do anything.
  const captchaServer = useQuery({
    queryKey: ['captcha', 'status'],
    queryFn: () => api.system.captcha(),
    staleTime: 5 * 60_000,
  });

  // Appearance is not part of the definition — it is a property of the form itself, so it
  // saves on change rather than riding the draft's debounce.
  const form = useQuery({
    queryKey: ['form', formId],
    queryFn: () => api.forms.get(formId),
    enabled: Boolean(formId),
  });

  const themes = useQuery({
    queryKey: ['themes'],
    queryFn: () => api.themes.list(),
    staleTime: 60_000,
  });

  const assignTheme = useMutation({
    mutationFn: (themeId: string | null) => api.themes.assign(formId, themeId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['form', formId] }),
  });

  useEffect(() => {
    if (draft.data && loadedFormId !== formId) load(formId, draft.data);
  }, [draft.data, formId, loadedFormId, load]);

  useEffect(() => {
    if (!dirty || !definition) return;
    const timer = setTimeout(async () => {
      await api.forms.saveDefinition(formId, definition);
      markSaved();
    }, 1200);
    return () => clearTimeout(timer);
  }, [dirty, definition, formId, markSaved]);

  if (!definition) return <div className="p-8"><Spinner /></div>;

  const settings = definition.settings;
  const protection = settings.protection;
  const server = captchaServer.data;
  const defaultTheme = (themes.data?.data ?? []).find((t) => t.isDefault);

  const setProtection = <K extends keyof typeof protection>(
    key: K, value: (typeof protection)[K],
  ) => apply(`Change ${String(key)}`, (d) => {
    // Older drafts predate requireInvite, so the group is spread rather than indexed
    // blindly — an undefined here would silently disable single-use enforcement.
    d.settings.protection = { ...d.settings.protection, [key]: value };
  });

  return (
    <Page>
      <PageHeader
        title="Settings"
        description="Saved automatically. Changes apply to the draft and take effect when
                     you publish."
      />

      <Stack>
      {/* ---- protection ---------------------------------------------------------- */}
      <Section
        title="Protection"
        hint="Layered: the free checks run first, and a captcha is only asked for when they
              flag something — so most respondents never see a widget."
        actions={server?.configured
          ? <Badge tone="success">{server.provider}</Badge>
          : <Badge tone="neutral">no captcha provider</Badge>}
      >
        <div className="mb-4">
          <Notice tone={server?.configured ? 'info' : 'warning'}>
            {server?.configured
              ? `Captcha tokens are verified server-side against ${server.provider}. Site key ${server.siteKeyPreview}.`
              : 'No provider is set up on this server, so the captcha setting below has no '
                + 'effect. The honeypot and timing checks still run.'}
          </Notice>
        </div>

        <Field
          label="Captcha"
          hint={
            protection.captcha === 'adaptive'
              ? 'Only challenges submissions the honeypot or timing check flagged. Recommended.'
              : protection.captcha === 'always'
                ? 'Every respondent sees a widget. Use for forms that are being actively abused.'
                : 'No captcha, whatever the server has configured.'
          }
        >
          <Select
            value={protection.captcha}
            onChange={(e) => setProtection('captcha', e.target.value as typeof protection.captcha)}
          >
            <option value="off">Off</option>
            <option value="adaptive">Adaptive — only when something looks wrong</option>
            <option value="always">Always</option>
          </Select>
        </Field>

        <div className="mt-3">
          <Checkbox
            label="Honeypot field"
            description="A decoy field hidden from people and from screen readers. Free, and it
                         catches indiscriminate bots before any third party is involved."
            checked={protection.honeypot}
            onChange={(v) => setProtection('honeypot', v)}
          />
        </div>

        <div className="mt-3">
          <Field
            label="Minimum completion time (seconds)"
            hint="Measured from when the server started the session, never from anything the
                  browser claims. Set to 0 for a one-question kiosk form where 3 seconds is
                  a realistic answer time."
          >
            <Input
              type="number"
              min={0}
              max={600}
              value={protection.minCompletionSeconds}
              onChange={(e) => setProtection('minCompletionSeconds', Number(e.target.value))}
            />
          </Field>
        </div>

        <div className="mt-3">
          <Checkbox
            label="Only accept responses through a single-use link"
            description="The form URL alone stops being enough to submit, which is what makes a
                         forwarded or leaked link harmless. Issue links on the Single-use links tab."
            checked={protection.requireInvite ?? false}
            onChange={(v) => setProtection('requireInvite', v)}
          />
        </div>

        <div className="mt-3">
          <Checkbox
            label="Require a signed embed token"
            description="The embed will not render without a valid HMAC token minted by your
                         backend. For forms shown only to authenticated users of a partner app."
            checked={protection.requireSignedToken}
            onChange={(v) => setProtection('requireSignedToken', v)}
          />
        </div>
      </Section>

      {/* ---- limits -------------------------------------------------------------- */}
      <Section title="Limits" hint="How many responses, and from whom.">

        <Field
          label="Response limit"
          hint="Blank for unlimited. Counted from sharded counters, so a burst can overshoot
                slightly rather than cutting off mid-transaction."
        >
          <Input
            type="number"
            min={1}
            value={settings.limits.responseLimit ?? ''}
            onChange={(e) => apply('Change response limit', (d) => {
              d.settings.limits.responseLimit = e.target.value === ''
                ? null
                : Number(e.target.value);
            })}
          />
        </Field>

        <div className="mt-3">
          <Field
            label="One response per"
            hint="Cookie is the least invasive and the easiest to defeat; identity requires a
                  signed embed token."
          >
            <Select
              value={settings.limits.onePerIdentity}
              onChange={(e) => apply('Change identity rule', (d) => {
                d.settings.limits.onePerIdentity =
                  e.target.value as typeof settings.limits.onePerIdentity;
              })}
            >
              <option value="none">No limit</option>
              <option value="cookie">Browser (cookie)</option>
              <option value="ip">IP address</option>
              <option value="identity">Signed identity</option>
              <option value="email">Email answer</option>
            </Select>
          </Field>
        </div>
      </Section>

      {/* ---- behaviour ----------------------------------------------------------- */}
      <Section title="Behaviour" hint="How the form behaves while somebody fills it in.">

        <Checkbox
          label="Save and resume"
          description="Answers are saved as the respondent types, and a resume link restores them."
          checked={settings.behaviour.saveAndResume}
          onChange={(v) => apply('Toggle save and resume', (d) => {
            d.settings.behaviour.saveAndResume = v;
          })}
        />

        <Checkbox
          label="Confirm before submitting"
          description="Adds a final step listing the answers, with a way back to change them."
          checked={settings.behaviour.confirmBeforeSubmit}
          onChange={(v) => apply('Toggle confirm', (d) => {
            d.settings.behaviour.confirmBeforeSubmit = v;
          })}
        />

        <Checkbox
          label="Shuffle questions"
          description="Randomises the order of questions within each page to remove order
                       bias. Section headers stay where you put them, and each respondent
                       keeps their own order if they resume."
          checked={settings.behaviour.shuffleQuestions}
          onChange={(v) => apply('Toggle shuffle', (d) => {
            d.settings.behaviour.shuffleQuestions = v;
          })}
        />

        <Checkbox
          label="Keep answers to questions that logic later hid"
          description="Off by default: an answer the respondent can no longer see is usually
                       one they would not expect you to keep."
          checked={settings.behaviour.retainHiddenAnswers}
          onChange={(v) => apply('Toggle retain hidden', (d) => {
            d.settings.behaviour.retainHiddenAnswers = v;
          })}
        />

        <Checkbox
          label="Number the questions"
          description="Numbered across the whole form rather than restarting on each page,
                       and questions hidden by logic are skipped so the numbers never gap."
          checked={settings.navigation.showQuestionNumbers}
          onChange={(v) => apply('Toggle question numbers', (d) => {
            d.settings.navigation.showQuestionNumbers = v;
          })}
        />

        <div className="mt-3">
          <Field label="Progress indicator">
            <Select
              value={settings.navigation.progressIndicator}
              onChange={(e) => apply('Change progress indicator', (d) => {
                d.settings.navigation.progressIndicator =
                  e.target.value as typeof settings.navigation.progressIndicator;
              })}
            >
              <option value="bar">Bar</option>
              <option value="steps">Steps</option>
              <option value="percent">Percent</option>
              <option value="none">None</option>
            </Select>
          </Field>
        </div>
      </Section>

      {/* ---- appearance ---------------------------------------------------------- */}
      <Section
        title="Appearance"
        hint="Applies to the hosted page and the embedded frame. Takes effect immediately —
              it is not part of the draft, so it does not wait for a publish."
      >
        <Field label="Theme">
          <Select
            value={form.data?.themeId ?? ''}
            disabled={assignTheme.isPending || themes.isLoading}
            onChange={(e) => assignTheme.mutate(e.target.value || null)}
          >
            <option value="">
              {defaultTheme ? `Workspace default (${defaultTheme.name})` : 'Built-in look'}
            </option>
            {(themes.data?.data ?? []).map((theme) => (
              <option key={theme.id} value={theme.id}>{theme.name}</option>
            ))}
          </Select>
        </Field>

        <p className="mt-2 text-xs text-[var(--ff-text-muted)]">
          <Link to="/themes" className="underline">Manage themes</Link> to change colours,
          type and shape.
        </p>
      </Section>
      </Stack>
    </Page>
  );
}
